import { OperationOutcomeError, operationOutcomeToString } from '@medplum/core';
import { formatMedplumClientError } from '../pages/appointments/utils/medplumClientErrors';
import { getUserFacingErrorMessage } from './userFacingError';

export const EXISTING_USER_MESSAGE =
  'A user with this email address already exists in the Unify system';

const GENERIC_PROVISIONING_FAILURE = 'Something went wrong. Please try again.';

export function isExistingUserErrorText(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes('already a member') ||
    lower.includes('different profile') ||
    lower.includes('already exists in this organization') ||
    lower.includes('already exists in the unify system')
  );
}

function parseBotExecuteErrorMessage(message: string): string | undefined {
  const trimmed = message.trim();
  if (!trimmed.startsWith('{')) {
    return undefined;
  }
  try {
    const obj = JSON.parse(trimmed) as { errorMessage?: string };
    if (typeof obj.errorMessage === 'string' && obj.errorMessage.length > 0) {
      return obj.errorMessage;
    }
  } catch {
    // Not JSON — use the raw message.
  }
  return undefined;
}

/** True when invite/add-client failed because the email is already provisioned. */
export function isExistingUserProvisioningError(err: unknown): boolean {
  if (err instanceof OperationOutcomeError) {
    return isExistingUserErrorText(operationOutcomeToString(err.outcome));
  }
  const raw = err instanceof Error ? err.message : String(err);
  const parsed = parseBotExecuteErrorMessage(raw);
  return isExistingUserErrorText(parsed ?? raw);
}

/** User-facing message for invite / add-client / create-user failures. */
export function formatUserProvisioningError(err: unknown): string {
  if (err instanceof OperationOutcomeError) {
    const text = operationOutcomeToString(err.outcome);
    if (isExistingUserErrorText(text)) {
      return EXISTING_USER_MESSAGE;
    }
    return getUserFacingErrorMessage(new Error(text), GENERIC_PROVISIONING_FAILURE);
  }

  const raw = err instanceof Error ? err.message : String(err);
  const parsed = parseBotExecuteErrorMessage(raw);
  const text = parsed ?? raw;

  if (isExistingUserErrorText(text)) {
    return EXISTING_USER_MESSAGE;
  }
  if (parsed) {
    return getUserFacingErrorMessage(new Error(parsed), GENERIC_PROVISIONING_FAILURE);
  }

  if (/^not found$/i.test(text.trim())) {
    return 'Invite failed (Not found). Re-register the practice-admin-actions bot, confirm AccessPolicies are seeded, and verify VITE_MEDPLUM_PROJECT_ID matches your login project.';
  }

  const formatted = formatMedplumClientError(err);
  if (formatted && formatted !== raw) {
    return getUserFacingErrorMessage(new Error(formatted), GENERIC_PROVISIONING_FAILURE);
  }

  return getUserFacingErrorMessage(new Error(text), GENERIC_PROVISIONING_FAILURE);
}
